import React from 'react';
import { MapPin, Calendar, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import hiseLokevImage from '@/assets/hise-lokev-day.jpeg';
import atriumSezanaImage from '@/assets/atrium-sezana.png';
import klinikaSezanaImage from '@/assets/klinika-sezana.jpg';

const Projects = () => {
  const navigate = useNavigate();
  
  // Current projects data
  const projects = [
    {
      id: 1,
      slug: "atrium-sezana",
      title: "Atrium Sežana",
      location: "Sežana, Slovenija",
      year: "2025",
      status: "V gradnji",
      type: "Butična stanovanja",
      description: "Butična stanovanja v kraškem slogu - kjer tradicija sreča sodobnost. 8 unikatnih stanovanj v prenovljeni kraški hiši.",
      details: "8 unikatnih stanovanj",
      image: atriumSezanaImage
    },
    {
      id: 2,
      slug: "hise-lokev",
      title: "Hiše Lokev",
      location: "Lokev, Slovenija", 
      year: "2026",
      status: "Pridobljeno GD",
      type: "Stanovanjske hiše",
      description: "3 stanovanjske enote od 130m² do 145m² na odlični lokaciji v Lokvi. Pridobljeno gradbeno dovoljenje, gradnja bo potekala v letu 2026.",
      details: "3 stanovanjske enote (130-145m²)",
      image: hiseLokevImage
    },
    {
      id: 3,
      slug: "poslovni-objekt-sezana",
      title: "Poslovni objekt Sežana",
      location: "Sežana, Slovenija",
      year: "2026-2027",
      status: "V pripravi",
      type: "Poslovni kompleks", 
      description: "Klinika, trgovina, poslovni apartmaji, penthouse. Skupna kvadratura cca 2000m² bruto. Predano za gradbeno dovoljenje.",
      details: "2000m² bruto",
      image: klinikaSezanaImage
    },
    {
      id: 4,
      slug: "poslovni-prostori-lokev",
      title: "Poslovni prostori Lokev",
      location: "Lokev, Slovenija",
      year: "2024", 
      status: "Prodano",
      type: "Obrtni prostori",
      description: "Hala z manjšimi enotami za obrtnike. Idealno za razvoj lokalnega podjetništva in obrti.",
      details: "Hala z obrtnimi enotami",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop"
    }
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'V gradnji':
        return 'bg-atren-orange/10 text-atren-orange';
      case 'Pridobljeno GD':
        return 'bg-green-100 text-green-800';
      case 'V pripravi':
        return 'bg-blue-100 text-blue-800';
      case 'V projektiranju':
        return 'bg-purple-100 text-purple-800';
      case 'Prodano':
        return 'bg-gray-200 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <section id="projekti" className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Naši <span className="text-atren-orange">projekti</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Poglejte si naše uspešno realizirane projekte in trenutne prodajne priložnosti. 
            Vsak projekt predstavlja našo zavezanost kakovosti in inovativnosti.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <Card 
              key={project.id} 
              className="group hover:shadow-elegant transition-all duration-300 border-0 bg-white overflow-hidden animate-scale-in"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              {/* Project Image */}
              <Link to={`/projekt/${project.slug}`} className="block relative h-64 overflow-hidden cursor-pointer">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-atren-gray/60 to-transparent"></div>
                
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  <Badge className={getStatusColor(project.status)}>
                    {project.status}
                  </Badge>
                </div>

                {/* Project Type */}
                <div className="absolute bottom-4 left-4">
                  <Badge variant="secondary" className="bg-white/90 text-atren-gray">
                    {project.type}
                  </Badge>
                </div>
              </Link>

              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-atren-orange transition-colors duration-300">
                  {project.title}
                </h3>
                
                {/* Location and Year */}
                <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {project.location}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {project.year}
                  </div>
                </div>

                <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                  {project.description}
                </p>

                {/* Project Details */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Obseg:</span>
                      <span className="ml-2 font-semibold text-foreground">{project.details}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/projekt/${project.slug}`)}
                      className="text-atren-orange-dark hover:text-atren-orange hover:bg-atren-orange/10"
                    >
                      Več <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 animate-fade-in-up">
          <div className="bg-gradient-subtle rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Iščete investicijsko priložnost?
            </h3>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              Pridružite se našim investitorjem in odkrijte priložnosti za donosen vložek v nepremičnine. 
              Pogovorite se z nami o trenutnih in prihodnjih projektih.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => scrollToSection('#kontakt')}
                className="bg-gradient-primary shadow-primary hover:shadow-elegant transition-all duration-300"
              >
                Investicijske priložnosti
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('#kontakt')}
                className="border-atren-orange text-atren-orange hover:bg-atren-orange hover:text-white transition-all duration-300"
              >
                Prodajni portfelj
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;